import queryString from "query-string";
import { useLocation } from "react-router-dom";
import type { LocationState } from "src/App";
import isString from "lodash/isString";

// We only allow redirecting back to the localhost server of the extension.
// Example of valid redirect_uri:
// 'http%3A%2F%2Flocalhost%3A58853%2Flogin'
// decodeURIComponent -> 'http://localhost:58853/login'

const validRedirectUriRegex = /^http:\/\/localhost:(?<port>\d+)\/login$/;
const validRedirectUris = [
  "/support", "/forums"
];

const minPortNumber = 1;
const maxPortNumber = 65535;

const isValidRedirectUri = (redirectUri: string | undefined) => {
  if (!isString(redirectUri)) {
    return false;
  }

  if (validRedirectUris.includes(redirectUri)) {
    return true;
  }

  const matches = redirectUri.match(validRedirectUriRegex);

  if (!matches) {
    return false;
  }

  const { port } = matches.groups as any as { port: number };

  return minPortNumber <= port && port <= maxPortNumber;
};

const includeRedirectParameters = (queryParams: queryString.ParsedQuery<string>) => Boolean(queryParams.include_params);

/**
 * Return a whitelisted redirect_uri, or undefined otherwise
 */
export const useRedirectUri = () => {
  const location = useLocation<LocationState | undefined>();
  const queryParams = queryString.parse(location.search);
  const redirectUri = queryParams.redirect_uri?.toString();

  if (!isValidRedirectUri(redirectUri)) {
    return undefined;
  }

  // In some cases we want to pass additional query parameters in redirect_uri
  // This is only done if include_params query parameter is set
  // Otherwise we return redirect_uri as is
  if (!includeRedirectParameters(queryParams)) {
    return redirectUri;
  }

  // Remove redirect_uri and include_params from query params
  // because we don't want to pass them to the redirect_uri,
  // as they're unnecessary in it.
  delete queryParams.redirect_uri;
  delete queryParams.include_params;

  let redirectQueryParams = "";

  // keep rest of the queryParams in redirect uri if any
  if (Object.keys(queryParams).length > 0) {
    redirectQueryParams = `?${queryString.stringify(queryParams)}`;
  }

  const redirectUriWithQueryParams = `${redirectUri}${redirectQueryParams}`;

  return redirectUriWithQueryParams;
};
